import {
	DocumentType,
	HumanProcessingStatus,
	SubmissionWithParticipantsAndValidDecennialCertificate,
} from "@dune/types";
import {
	useLinkFileToConstructionSiteSubmission,
	useUnlinkFileFromConstructionSiteSubmission,
} from "../../api/mutations/constructionSiteSubmission.mutations";
import { FileStatusChip } from "../common/files/FileStatusChip";
import { PreviewFileLink } from "../common/files/PreviewFileLink";
import { AddDocumentButton } from "../constructionSites/submission/AddDocumentButton";
import { Button } from "@dune/ui";
import { LinkBroken02 } from "@untitled-ui/icons-react";

type RequiredDocumentsTypes = DocumentType;

interface ParticipantDocumentsProps {
	submissionParticipant: SubmissionWithParticipantsAndValidDecennialCertificate["participantToConstructionSiteSubmissions"][number];
	constructionSiteId: number;
	readonly?: boolean;
}

const getDocumentLabel = (documentType: RequiredDocumentsTypes): string => {
	const labels: Partial<Record<RequiredDocumentsTypes, string>> = {
		ATTESTATION_DECENNALE: "Attestation Décennale",
		CONVENTION_HONORAIRES: "Convention d'honoraires",
		DEVIS: "Devis",
		FACTURE: "Facture",
		RAPPORT_INITIAL_CONTROLE_TECHNIQUE: "Rapport",
		RAPPORT_ETUDE_TECHNIQUE: "Rapport",
		RAPPORT_GEOTECHNIQUE: "Rapport",
	};
	return labels[documentType] || documentType.replace(/_/g, " ").toLowerCase();
};

export const ParticipantDocuments = ({
	submissionParticipant,
	constructionSiteId,
	readonly = false,
}: ParticipantDocumentsProps) => {
	const requiredDocuments: RequiredDocumentsTypes[] = [];
	const designerType = submissionParticipant.designerType;
	const participant = submissionParticipant.participant;

	if (submissionParticipant.type === "DESIGNER") {
		requiredDocuments.push("CONVENTION_HONORAIRES", "ATTESTATION_DECENNALE");
		if (designerType === "GEOTECHNICIEN") {
			requiredDocuments.push("RAPPORT_GEOTECHNIQUE");
		}
		if (designerType === "BUREAU_ETUDE") {
			requiredDocuments.push("RAPPORT_ETUDE_TECHNIQUE");
		}
		if (designerType === "CONTROLEUR_TECHNIQUE") {
			requiredDocuments.push("RAPPORT_INITIAL_CONTROLE_TECHNIQUE");
		}
	} else if (submissionParticipant.type === "BUILDER") {
		requiredDocuments.push("DEVIS", "ATTESTATION_DECENNALE");
	} else if (submissionParticipant.type === "MATERIAL") {
		requiredDocuments.push("FACTURE");
	}

	const findFilesForType = (documentType: RequiredDocumentsTypes) => {
		const files: {
			id: string;
			key: string;
			bucket: string;
			mimeType: string;
			name: string;
			humanProcessingStatus: HumanProcessingStatus;
		}[] = submissionParticipant.participant.filesOnConstructionSiteSubmission
			.filter((file) => file.type === documentType)
			.map((file) => file.file);

		if (documentType === "ATTESTATION_DECENNALE") {
			files.push(
				...(submissionParticipant.decennialCertificatesOfSubmissionOrValidFromOthers?.map(
					(cert) => cert.file,
				) || []),
			);
		}
		return files;
	};

	const linkFileMutation =
		useLinkFileToConstructionSiteSubmission(constructionSiteId);

	const unlinkFileMutation =
		useUnlinkFileFromConstructionSiteSubmission(constructionSiteId);

	return (
		<div className="flex flex-col gap-4">
			{requiredDocuments.map((documentType) => {
				const files = findFilesForType(documentType);
				return (
					<div key={documentType} className="flex flex-col gap-2">
						<p className="text-sm font-medium">
							{getDocumentLabel(documentType)}
						</p>
						<div className="flex flex-col gap-2">
							{files.length > 0
								? files.map((file) => (
										<div
											key={file.id}
											className="flex gap-2 items-center justify-between min-w-0"
										>
											<PreviewFileLink
											file={file}
											className="flex-1 overflow-hidden"
										/>
											<div className="flex items-center shrink-0">
												<FileStatusChip
													humanProcessingStatus={file.humanProcessingStatus}
												/>
												{!readonly && (
													<Button
														variant="ghost"
														size="sm"
														onClick={() => {
															unlinkFileMutation.mutate(file.id);
														}}
													>
														<LinkBroken02 className="h-4 w-4" />
													</Button>
												)}
											</div>
										</div>
									))
								: null}
							{!readonly && (
								<div className="flex justify-start">
									<AddDocumentButton
										constructionSiteId={constructionSiteId}
										onUploadSuccess={(fileId) => {
											linkFileMutation.mutate({
												fileId,
												type: documentType,
												participantId: participant.id,
											});
										}}
									/>
								</div>
							)}
						</div>
					</div>
				);
			})}
		</div>
	);
};
